import React, { createContext, FunctionComponent, useContext, useEffect, useRef, useState } from 'react'
import { fetchChannel } from 'shared/api/channels'
import useAsyncEffect from 'shared/hooks/use-async-effect'
import { IChannel } from 'shared/types/channel'
import RefreshDataContext from '../context/refresh-data'

export type ChannelMap = {
    [id: number]: IChannel
}
type CollectionContextProps = {
    rootChannelId: number
    channels: ChannelMap
    getChannelChildren(channel: IChannel, update?: boolean): Promise<IChannel[]>
}
export const CollectionContext = createContext({} as CollectionContextProps)

export function flatten(children: IChannel[]) {
    let map: ChannelMap = {}
    children.forEach(channel => {
        map[channel.id] = channel
        if (channel.children) {
            map = { ...map, ...flatten(channel.children) }
        }
    })
    return map
}

export const fakeRootChannelId = 0
const makeRootChannel = (rootChildren: IChannel[]) => {
    const children = rootChildren.map(channel => ({ ...channel, parent: fakeRootChannelId }))
    return {
        children,
        id: fakeRootChannelId,
        has_children: children.length > 0,
        display_name: 'Root',
    } as IChannel
}

type Props = { id: number; existingChannels?: ChannelMap; showRoot?: boolean }

// manage and populate the context value
const CollectionContextManager: FunctionComponent<Props> = ({
    id = 0,
    existingChannels = {},
    showRoot = false,
    children,
}) => {
    const [, setUpdate] = useState()
    const channels = useRef<ChannelMap>(existingChannels)
    const setChannels = (c: ChannelMap) => {
        channels.current = c
        setUpdate({} as any)
    }
    const { onRefreshChannel } = useContext(RefreshDataContext)
    const getChannelChildren = async (channel: IChannel) => {
        if (channel.has_children) {
            // fetching a channel will give you its children
            // if we have different Collection Managers,
            // they won't make the same request because fetching is cached
            const fetchedChannel = await fetchChannel(channel.id)
            const newChildren = fetchedChannel.children || []
            // put it in context
            setChannels({ ...channels.current, ...flatten(newChildren), [fetchedChannel.id]: fetchedChannel })
            // but also return them to use them inline
            return newChildren
        }
        return channel.children || []
    }

    useEffect(() => {
        const offRefresh = onRefreshChannel((channel: IChannel) => {
            setChannels({ ...channels.current, [channel.id]: channel })
        })
        return offRefresh
    }, [])

    useAsyncEffect(async () => {
        if (!existingChannels[id]) {
            const channel = await fetchChannel(id)
            // we fetched a single channel, do we want to show its children or itself?
            setChannels(flatten([showRoot ? makeRootChannel([channel]) : channel]))
        }
    }, [id, showRoot])
    return channels.current[id] ? (
        <CollectionContext.Provider
            value={{ channels: channels.current, getChannelChildren, rootChannelId: showRoot ? fakeRootChannelId : id }}
        >
            {children}
        </CollectionContext.Provider>
    ) : null
}

export default CollectionContextManager
